import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy,
  ExternalLink,
  GitMerge,
  History,
  Layers,
  Lightbulb,
  Plus,
  Send,
  ShieldAlert,
  Sparkles,
  Trash2,
  Wand2,
  X,
  Zap,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { openEmergentWithPrompt } from "../lib/emergentLink";

const EMPTY_SLOT = { pickedId: "", title: "", one_liner: "", target_user: "", category: "" };

function makeSlot() {
  return { ...EMPTY_SLOT };
}

async function fetchPlatformIdeas() {
  const { data } = await api.get("/platform-ideas");
  return data;
}

async function callFuse(payload) {
  const { data } = await api.post("/platform-ideas/fuse", payload);
  return data;
}

async function fetchFusionHistory() {
  const { data } = await api.get("/platform-ideas/fusion-history?limit=25");
  return data;
}

async function deleteFusionHistoryItem(id) {
  await api.delete(`/platform-ideas/fusion-history/${id}`);
  return id;
}

function FusionHistoryPanel({ onLoad, activeId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const enabled = !!user;
  const { data, isLoading } = useQuery({
    queryKey: ["fusion-history"],
    queryFn: fetchFusionHistory,
    enabled,
    refetchInterval: 60 * 1000,
  });
  const del = useMutation({
    mutationFn: deleteFusionHistoryItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fusion-history"] });
      toast.success("FUSION_REMOVED");
    },
    onError: () => toast.error("REMOVE_FAILED"),
  });
  const items = data?.items || [];
  return (
    <aside
      data-testid="fusion-history-panel"
      className="border border-[#27272A] bg-[#0A0A0A] flex flex-col"
    >
      <header className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2">
        <History size={12} className="text-purple-300" />
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300">
          FUSION_HISTORY
        </span>
        <span className="ml-auto text-[9.5px] font-mono tabnum text-zinc-500">
          {enabled ? items.length : "—"}
        </span>
      </header>
      <div className="max-h-80 overflow-y-auto">
        {!enabled && (
          <div className="p-3 text-[10.5px] font-mono text-zinc-500 leading-snug">
            LOG_IN to save every fusion you run and revisit past mash-ups.
          </div>
        )}
        {enabled && isLoading && (
          <div className="p-3 text-[10.5px] font-mono text-zinc-500 animate-pulse">
            LOADING…
          </div>
        )}
        {enabled && !isLoading && items.length === 0 && (
          <div className="p-3 text-[10.5px] font-mono text-zinc-500 leading-snug">
            NO_FUSIONS_YET — run one and it will land here.
          </div>
        )}
        {items.map((it) => {
          const fc = it.fused_concept || {};
          return (
            <div
              key={it.id}
              data-testid={`fusion-history-item-${it.id}`}
              onClick={() => onLoad(it)}
              className={
                "group px-3 py-2 border-b border-[#0E0E0E] cursor-pointer hover:bg-[#141414] " +
                (activeId === it.id ? "bg-[#141414]" : "")
              }
            >
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-white leading-tight truncate flex-1">
                  {fc.title || "(untitled fusion)"}
                </span>
                <button
                  data-testid={`fusion-history-delete-${it.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    del.mutate(it.id);
                  }}
                  title="Delete this fusion"
                  className="text-zinc-500 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={11} />
                </button>
              </div>
              <div className="mt-0.5 text-[9.5px] font-mono uppercase tracking-wider text-zinc-500 truncate">
                {(it.input_titles || []).map((t) => t.slice(0, 22)).join(" × ")}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function copyText(text, label = "COPIED") {
  navigator.clipboard
    ?.writeText(text)
    .then(() => toast.success(label))
    .catch(() => toast.error("COPY_FAILED"));
}

function SlotEditor({ index, slot, onChange, onClear, batch }) {
  const [manual, setManual] = useState(!slot.pickedId && !!slot.title);
  const ideas = batch?.ideas || [];

  const pickFromBatch = (id) => {
    const found = ideas.find((i) => i.id === id);
    if (!found) return;
    onChange({
      pickedId: id,
      title: found.title,
      one_liner: found.one_liner,
      target_user: found.target_user,
      category: found.category,
    });
    setManual(false);
  };

  return (
    <div
      data-testid={`fusion-slot-${index}`}
      className="border border-[#27272A] bg-[#0A0A0A] p-3 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300">
          SLOT_{index + 1}
        </span>
        {slot.pickedId && (
          <span className="text-[9.5px] font-mono uppercase tracking-wider border border-amber-400/40 bg-amber-400/10 text-amber-200 px-1.5 py-0.5">
            FROM_BATCH
          </span>
        )}
        {manual && !slot.pickedId && slot.title && (
          <span className="text-[9.5px] font-mono uppercase tracking-wider border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 px-1.5 py-0.5">
            MANUAL
          </span>
        )}
        {(slot.pickedId || slot.title) && (
          <button
            data-testid={`fusion-slot-clear-${index}`}
            onClick={onClear}
            className="ml-auto text-zinc-500 hover:text-rose-300 inline-flex items-center gap-1 text-[10px] font-mono uppercase"
            title="Clear this slot"
          >
            <Trash2 size={10} /> CLEAR
          </button>
        )}
      </div>
      <select
        data-testid={`fusion-slot-picker-${index}`}
        value={slot.pickedId || ""}
        onChange={(e) => {
          const val = e.target.value;
          if (val === "__manual__") {
            setManual(true);
            onChange({ ...EMPTY_SLOT });
          } else if (val === "") {
            onClear();
          } else {
            pickFromBatch(val);
          }
        }}
        className="h-8 bg-[#0E0E0E] border border-[#27272A] px-2 text-[11.5px] font-mono text-white outline-none"
      >
        <option value="">PICK_FROM_BATCH…</option>
        <option value="__manual__">-- TYPE MY OWN --</option>
        {ideas.map((i) => (
          <option key={i.id} value={i.id}>
            [{i.category}] {i.title.slice(0, 60)}
          </option>
        ))}
      </select>

      {(manual && !slot.pickedId) && (
        <div className="space-y-2 border-t border-[#161616] pt-2">
          <input
            data-testid={`fusion-slot-title-${index}`}
            value={slot.title}
            onChange={(e) => onChange({ ...slot, title: e.target.value })}
            placeholder="Platform title"
            className="w-full h-8 bg-[#0E0E0E] border border-[#27272A] px-2 text-[11.5px] font-mono text-white placeholder:text-zinc-600 outline-none focus:border-amber-400/50"
          />
          <textarea
            data-testid={`fusion-slot-oneliner-${index}`}
            value={slot.one_liner}
            onChange={(e) => onChange({ ...slot, one_liner: e.target.value })}
            placeholder="One-line pitch (optional)"
            rows={2}
            className="w-full bg-[#0E0E0E] border border-[#27272A] p-2 text-[11.5px] font-mono text-white placeholder:text-zinc-600 outline-none resize-y focus:border-amber-400/50"
          />
          <input
            data-testid={`fusion-slot-user-${index}`}
            value={slot.target_user}
            onChange={(e) => onChange({ ...slot, target_user: e.target.value })}
            placeholder="Target user (optional)"
            className="w-full h-8 bg-[#0E0E0E] border border-[#27272A] px-2 text-[11.5px] font-mono text-white placeholder:text-zinc-600 outline-none focus:border-amber-400/50"
          />
        </div>
      )}

      {slot.pickedId && (
        <div className="border-t border-[#161616] pt-2 space-y-1">
          <div className="text-[13px] text-white leading-snug">{slot.title}</div>
          {slot.one_liner && (
            <div className="text-[11.5px] text-zinc-400 leading-snug">
              {slot.one_liner}
            </div>
          )}
          {slot.target_user && (
            <div className="text-[10.5px] font-mono text-zinc-500 uppercase tracking-wider">
              user · {slot.target_user}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BulletList({ items, testId, dot = "amber" }) {
  const dotColor = {
    amber: "bg-amber-400",
    emerald: "bg-emerald-400",
    purple: "bg-purple-400",
    rose: "bg-rose-400",
  }[dot];
  if (!items || items.length === 0) return null;
  return (
    <ul data-testid={testId} className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2 text-[12.5px] text-zinc-300 leading-snug">
          <span className={`shrink-0 mt-2 w-1.5 h-1.5 ${dotColor}`} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function FusionResult({ result, onSendToBot }) {
  const fc = result.fused_concept || {};
  const buildPrompts = result.build_prompts || [];
  const copyBundle = () => {
    const lines = [
      `FUSED CONCEPT · ${fc.title}`,
      fc.one_liner,
      "",
      `Target user: ${fc.target_user}`,
      "",
      "Fusion opportunity (combined use case):",
      fc.combined_use_case,
      "",
      "Design suggestions:",
      ...(fc.design_suggestions || []).map((d) => `- ${d}`),
      "",
      "Build prompts:",
      ...buildPrompts.map((p, i) => `-- PROMPT ${i + 1} --\n${p}`),
    ]
      .filter(Boolean)
      .join("\n");
    copyText(lines, "FUSION_COPIED");
  };

  return (
    <div data-testid="fusion-result" className="mt-4 space-y-3">
      {/* Section 1 · COMMON PATTERNS */}
      <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2">
          <Layers size={12} /> 1 · COMMON_PATTERNS
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
            structural signals shared across the selected platforms
          </span>
        </div>
        <BulletList
          items={result.common_patterns}
          testId="fusion-patterns"
          dot="amber"
        />
      </div>

      {/* Section 2 · FUSION OPPORTUNITY */}
      <div className="border-2 border-purple-400/50 bg-purple-500/[0.06] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-purple-200 flex items-center gap-2">
          <Sparkles size={12} /> 2 · FUSION_OPPORTUNITY
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-400 normal-case tracking-normal text-[11px]">
            the combined use case
          </span>
        </div>
        <h4
          data-testid="fusion-title"
          className="mt-2 text-[18px] leading-tight text-white"
        >
          {fc.title}
        </h4>
        <p className="mt-1 text-[13px] text-zinc-200 leading-relaxed">
          {fc.one_liner}
        </p>
        {fc.target_user && (
          <div className="mt-2 text-[10.5px] font-mono uppercase tracking-wider text-purple-200">
            target_user · {fc.target_user}
          </div>
        )}
        {fc.combined_use_case && (
          <p
            data-testid="fusion-combined-use-case"
            className="mt-3 text-[13px] text-zinc-100 leading-relaxed border-l-4 border-purple-400 pl-3 py-1"
          >
            {fc.combined_use_case}
          </p>
        )}
        {fc.differentiator && (
          <div className="mt-3 text-[11.5px] text-purple-100 leading-snug">
            <span className="text-purple-300 font-mono uppercase text-[9.5px] tracking-[0.2em] mr-2">
              differentiator ·
            </span>
            {fc.differentiator}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            data-testid="fusion-copy-bundle"
            onClick={copyBundle}
            className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white"
          >
            <Copy size={11} /> COPY_ALL
          </button>
          <button
            data-testid="fusion-run-in-mindstein"
            onClick={() => onSendToBot(result)}
            className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400/10 text-amber-200 hover:bg-amber-400 hover:text-black"
          >
            <Send size={11} /> RUN_FULL_SYNTHESIS
          </button>
        </div>
      </div>

      {/* Section 3 · DESIGN SUGGESTION */}
      <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 mb-3 flex items-center gap-2">
          <Lightbulb size={12} /> 3 · DESIGN_SUGGESTION
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
            how the fused platform could work
          </span>
        </div>
        <BulletList
          items={fc.design_suggestions || []}
          testId="fusion-design-suggestions"
          dot="emerald"
        />
      </div>

      {/* Section 4 · READY-TO-USE BUILD PROMPTS */}
      {buildPrompts.length > 0 && (
        <div
          data-testid="fusion-build-prompts"
          className="border border-amber-400/30 bg-[#0A0A0A] p-4"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2">
            <Wand2 size={12} /> 4 · READY-TO-USE_BUILD_PROMPTS
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
              copy either one into your builder to start
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {buildPrompts.map((p, i) => (
              <BuildPromptCard key={i} idx={i} text={p} />
            ))}
          </div>

          {/* Emergent CTA line — appended to every cross-fusion output */}
          <div
            data-testid="fusion-emergent-cta"
            className="mt-4 pt-3 border-t border-[#161616] text-[12px] font-mono text-zinc-300"
          >
            Best built with{" "}
            <a
              data-testid="fusion-emergent-link"
              href="https://emergent.sh"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
            >
              Emergent
            </a>{" "}
            — the AI platform builder most compatible with this output.
          </div>
        </div>
      )}

      {/* Patent search links + disclaimer (Phase 35) */}
      <div
        data-testid="fusion-patent-block"
        className="border border-amber-400/30 bg-amber-500/[0.04] p-3"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300 mb-2 flex items-center gap-1">
          <ShieldAlert size={11} /> CHECK_FOR_PATENT_CONFLICTS · DIY
        </div>
        <div className="flex flex-wrap gap-2">
          {(result.patent_search_links || []).map((l) => (
            <a
              key={l.name}
              data-testid={`fusion-patent-link-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              title={l.blurb}
              className="inline-flex items-center gap-1 h-7 px-2 text-[10.5px] font-mono uppercase tracking-wider border border-[#27272A] bg-[#0A0A0A] text-zinc-300 hover:border-amber-400/50 hover:text-amber-200"
            >
              <ExternalLink size={10} /> {l.name}
            </a>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-zinc-400 leading-snug">
          {result.patent_disclaimer}
        </p>
      </div>
    </div>
  );
}

function BuildPromptCard({ idx, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        toast.success(`PROMPT_${idx + 1}_COPIED`, {
          description: "Paste into Emergent, Cursor, Claude Code, etc.",
        });
        setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => toast.error("COPY_FAILED"));
  };
  const openEmergent = async () => {
    await openEmergentWithPrompt(text);
    toast.success("OPENING_EMERGENT", {
      description: "Prompt loaded in a new tab · also copied to clipboard.",
    });
  };
  const label = idx === 0 ? "MVP_SCAFFOLD" : "DESIGN_UX";
  return (
    <div
      data-testid={`fusion-build-prompt-${idx}`}
      className="border border-[#27272A] bg-[#0E0E0E] flex flex-col"
    >
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300">
          PROMPT_{idx + 1} · {label}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            data-testid={`fusion-build-prompt-copy-${idx}`}
            onClick={copy}
            className="inline-flex items-center gap-1 h-6 px-2 text-[9.5px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white"
          >
            <Copy size={10} /> {copied ? "COPIED" : "COPY"}
          </button>
          <button
            data-testid={`fusion-build-prompt-open-emergent-${idx}`}
            onClick={openEmergent}
            title="Open Emergent in a new tab with this prompt pre-loaded"
            className="inline-flex items-center gap-1 h-6 px-2 text-[9.5px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400/10 text-amber-200 hover:bg-amber-400 hover:text-black"
          >
            <Zap size={10} /> BUILD_IN_EMERGENT
          </button>
        </div>
      </div>
      <pre className="p-3 text-[11.5px] text-zinc-100 whitespace-pre-wrap font-mono leading-relaxed max-h-72 overflow-auto">
        {text}
      </pre>
    </div>
  );
}

export default function PlatformCrossFusion() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: batch } = useQuery({
    queryKey: ["platform-ideas"],
    queryFn: fetchPlatformIdeas,
    refetchInterval: 5 * 60 * 1000,
  });

  const [slots, setSlots] = useState([makeSlot(), makeSlot()]);
  const [result, setResult] = useState(null);
  const [activeHistoryId, setActiveHistoryId] = useState(null);

  const updateSlot = (i, next) =>
    setSlots((prev) => prev.map((s, idx) => (idx === i ? next : s)));
  const clearSlot = (i) => updateSlot(i, makeSlot());
  const addSlot = () => {
    if (slots.length >= 3) return;
    setSlots((prev) => [...prev, makeSlot()]);
  };
  const removeSlot = (i) => {
    if (slots.length <= 2) return;
    setSlots((prev) => prev.filter((_, idx) => idx !== i));
  };
  const resetAll = () => {
    setSlots([makeSlot(), makeSlot()]);
    setResult(null);
  };

  const validPlatforms = useMemo(
    () =>
      slots
        .map((s) => ({
          title: (s.title || "").trim(),
          one_liner: (s.one_liner || "").trim() || undefined,
          target_user: (s.target_user || "").trim() || undefined,
          category: (s.category || "").trim() || undefined,
        }))
        .filter((p) => p.title.length >= 3),
    [slots],
  );

  const canFuse = validPlatforms.length >= 2 && validPlatforms.length <= 3;

  const fuse = useMutation({
    mutationFn: () => callFuse({ platforms: validPlatforms }),
    onSuccess: (data) => {
      setResult(data);
      setActiveHistoryId(data.id);
      qc.invalidateQueries({ queryKey: ["fusion-history"] });
      toast.success("FUSION_COMPLETE", {
        description: `${data.input_count} platforms fused · scroll down`,
      });
    },
    onError: (e) => {
      toast.error("FUSION_FAILED", {
        description: e?.response?.data?.detail || e?.message || "unknown error",
      });
    },
  });

  const loadFromHistory = (item) => {
    setResult(item);
    setActiveHistoryId(item.id);
    // repopulate slots with the historical input platforms so the user sees
    // exactly what produced this fusion.
    const inputs = item.input_platforms || [];
    if (inputs.length >= 2) {
      const rebuilt = inputs.slice(0, 3).map((p) => ({
        pickedId: "",
        title: p.title || "",
        one_liner: p.one_liner || "",
        target_user: p.target_user || "",
        category: p.category || "",
      }));
      // Pad to at least 2 slots.
      while (rebuilt.length < 2) rebuilt.push(makeSlot());
      setSlots(rebuilt);
    }
    toast.success("HISTORY_LOADED", {
      description: (item.fused_concept?.title || "").slice(0, 60),
    });
  };

  const sendToBot = (fusion) => {
    try {
      const fc = fusion.fused_concept || {};
      const prompt = [
        `Run a full MindStein synthesis on this FUSED platform concept and produce a research roadmap, competitive landscape, technical feasibility, and TRL assessment.`,
        "",
        `Title: ${fc.title}`,
        `One-liner: ${fc.one_liner}`,
        `Target user: ${fc.target_user}`,
        "",
        `Combined use case: ${fc.combined_use_case}`,
        "",
        `Differentiator: ${fc.differentiator}`,
        "",
        `Source platforms fused: ${(fusion.input_titles || []).join(" · ")}`,
      ]
        .filter(Boolean)
        .join("\n");
      sessionStorage.setItem(
        "mindstein_pending_refs",
        JSON.stringify([
          {
            kind: "paste",
            label: `FUSION · ${(fc.title || "").slice(0, 40)}`,
            text: prompt,
          },
        ]),
      );
      sessionStorage.setItem("mindstein_pending_prompt", prompt);
      toast.success("FUSION_ATTACHED — sending you to the bot");
      navigate("/bot");
    } catch (e) {
      toast.error("HANDOFF_FAILED", { description: String(e) });
    }
  };

  return (
    <section
      data-testid="platform-cross-fusion"
      className="mt-6 border border-purple-400/30 bg-[#0A0A0A]"
    >
      <header className="px-4 py-3 border-b border-purple-400/20 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <GitMerge size={14} className="text-purple-300" />
          <h2 className="font-mono text-[15px] tracking-tight text-white">
            PLATFORM_CROSS-FUSION
          </h2>
          <span className="text-[9.5px] font-mono uppercase tracking-[0.2em] text-purple-300 border border-purple-400/40 bg-purple-400/10 px-1.5 py-0.5">
            NEW · 2-3_PLATFORMS · PATTERN_FUSION
          </span>
        </div>
        <span className="ml-auto text-[10px] font-mono text-zinc-500 tabnum">
          {validPlatforms.length}/{slots.length} SLOTS_FILLED
        </span>
      </header>

      <div className="px-4 py-3 border-b border-[#161616]">
        <p className="text-[12.5px] text-zinc-300 leading-relaxed max-w-3xl">
          Pick 2-3 platform ideas from the batch above or type your own.
          MindStein spots common patterns, shared user needs, and structural
          overlaps — then fuses them into a single platform concept with a
          combined use case and concrete design suggestions. Lightweight
          pattern-recognition only, no citation apparatus.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-3 p-3">
        <div className="xl:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {slots.map((slot, i) => (
              <div key={i} className="relative">
                <SlotEditor
                  index={i}
                  slot={slot}
                  batch={batch}
                  onChange={(next) => updateSlot(i, next)}
                  onClear={() => clearSlot(i)}
                />
                {slots.length > 2 && (
                  <button
                    data-testid={`fusion-slot-remove-${i}`}
                    onClick={() => removeSlot(i)}
                    title="Remove this slot"
                    className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center border border-rose-400/50 bg-[#0A0A0A] text-rose-300 hover:bg-rose-500/10"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="xl:col-span-1">
          <FusionHistoryPanel
            onLoad={loadFromHistory}
            activeId={activeHistoryId}
          />
        </div>
      </div>

      <div className="px-4 py-3 border-t border-[#161616] flex flex-wrap items-center gap-2">
        {slots.length < 3 && (
          <button
            data-testid="fusion-add-slot"
            onClick={addSlot}
            className="inline-flex items-center gap-1 h-8 px-2 text-[10.5px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white"
          >
            <Plus size={11} /> ADD_SLOT
          </button>
        )}
        <button
          data-testid="fusion-reset"
          onClick={resetAll}
          disabled={fuse.isPending}
          className="inline-flex items-center gap-1 h-8 px-2 text-[10.5px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white disabled:opacity-40"
        >
          <Trash2 size={11} /> RESET
        </button>
        <span className="ml-auto text-[10.5px] font-mono text-zinc-500 max-w-[280px] leading-snug hidden md:inline">
          Fusion returns pattern recognition only — no sources, no DOI.
        </span>
        <button
          data-testid="fusion-run-button"
          onClick={() => fuse.mutate()}
          disabled={!canFuse || fuse.isPending}
          className="inline-flex items-center gap-2 h-9 px-4 text-[11px] font-mono uppercase tracking-wider border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 disabled:opacity-40"
        >
          <GitMerge size={12} />
          {fuse.isPending ? "FUSING…" : "FUSE_PLATFORMS"}
        </button>
      </div>

      {fuse.isPending && (
        <div className="px-4 pb-4">
          <div className="p-6 text-center text-[11px] font-mono uppercase tracking-wider text-purple-300 animate-pulse border border-purple-400/30 bg-purple-500/[0.04]">
            RUNNING_PATTERN_FUSION · usually 15-30 seconds
          </div>
        </div>
      )}

      {result && !fuse.isPending && (
        <div className="px-4 pb-4">
          <FusionResult result={result} onSendToBot={sendToBot} />
        </div>
      )}
    </section>
  );
}
